/**
 * Secure Storage Service
 *
 * Provides a secure wrapper around browser localStorage with optional obfuscation.
 * All keys are prefixed to avoid collisions with other applications.
 * Sensitive data can be obfuscated (not true encryption, but better than plain text).
 *
 * Note: This provides basic security. For production, consider using httpOnly cookies
 * for sensitive tokens, as localStorage is vulnerable to XSS attacks.
 *
 * @example
 * ```typescript
 * constructor(private secureStorage: SecureStorageService) {}
 *
 * // Store sensitive data
 * this.secureStorage.setItem('token', 'jwt-token-here', true);
 *
 * // Retrieve sensitive data
 * const token = this.secureStorage.getItem<string>('token', true);
 *
 * // Remove data
 * this.secureStorage.removeItem('token');
 * ```
 */

import { Injectable } from '@angular/core';

/**
 * Prefix for all storage keys to avoid collisions
 */
const STORAGE_PREFIX = 'blueride_company_';

@Injectable({
  providedIn: 'root'
})
export class SecureStorageService {

  /**
   * Stores an item in localStorage with optional obfuscation
   *
   * @param key - Storage key (will be prefixed automatically)
   * @param value - Value to store (will be JSON stringified)
   * @param obfuscate - Whether to obfuscate the value (default: false)
   *
   * @example
   * ```typescript
   * // Store plain data
   * this.secureStorage.setItem('user-id', 123);
   *
   * // Store obfuscated data
   * this.secureStorage.setItem('auth-token', 'secret-token', true);
   * ```
   */
  setItem(key: string, value: any, obfuscate: boolean = false): void {
    try {
      const prefixedKey = this.getPrefixedKey(key);
      let dataToStore = JSON.stringify(value);

      if (obfuscate) {
        dataToStore = this.obfuscate(dataToStore);
      }

      localStorage.setItem(prefixedKey, dataToStore);
    } catch (error) {
      console.error(`Failed to save item to secure storage (key: ${key}):`, error);
    }
  }

  /**
   * Retrieves an item from localStorage with optional deobfuscation
   *
   * @param key - Storage key (will be prefixed automatically)
   * @param deobfuscate - Whether to deobfuscate the value (default: false)
   * @returns The stored value or null if not found
   *
   * @example
   * ```typescript
   * // Retrieve plain data
   * const userId = this.secureStorage.getItem<number>('user-id');
   *
   * // Retrieve obfuscated data
   * const token = this.secureStorage.getItem<string>('auth-token', true);
   * ```
   */
  getItem<T>(key: string, deobfuscate: boolean = false): T | null {
    try {
      const prefixedKey = this.getPrefixedKey(key);
      let data = localStorage.getItem(prefixedKey);

      if (data === null) {
        return null;
      }

      if (deobfuscate) {
        const deobfuscated = this.deobfuscate(data);
        if (deobfuscated === null) {
          // Deobfuscation failed, data is corrupted - clear it
          this.removeItem(key);
          return null;
        }
        data = deobfuscated;
      }

      return JSON.parse(data) as T;
    } catch {
      // JSON parse failed, data is corrupted - clear it
      this.removeItem(key);
      return null;
    }
  }

  /**
   * Removes an item from localStorage
   *
   * @param key - Storage key (will be prefixed automatically)
   *
   * @example
   * ```typescript
   * this.secureStorage.removeItem('auth-token');
   * ```
   */
  removeItem(key: string): void {
    try {
      const prefixedKey = this.getPrefixedKey(key);
      localStorage.removeItem(prefixedKey);
    } catch (error) {
      console.error(`Failed to remove item from secure storage (key: ${key}):`, error);
    }
  }

  /**
   * Clears all items with the application prefix from localStorage
   *
   * This only removes items created by this application, not all localStorage items.
   *
   * @example
   * ```typescript
   * this.secureStorage.clear();
   * ```
   */
  clear(): void {
    try {
      const keysToRemove: string[] = [];

      // Find all keys with our prefix
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(STORAGE_PREFIX)) {
          keysToRemove.push(key);
        }
      }

      // Remove all found keys
      keysToRemove.forEach(key => localStorage.removeItem(key));
    } catch (error) {
      console.error('Failed to clear secure storage:', error);
    }
  }

  /**
   * Checks if an item exists in storage
   *
   * @param key - Storage key (will be prefixed automatically)
   * @returns True if the item exists, false otherwise
   *
   * @example
   * ```typescript
   * if (this.secureStorage.hasItem('auth-token')) {
   *   console.log('Token exists');
   * }
   * ```
   */
  hasItem(key: string): boolean {
    const prefixedKey = this.getPrefixedKey(key);
    return localStorage.getItem(prefixedKey) !== null;
  }

  /**
   * Adds the storage prefix to a key
   *
   * @param key - Original key
   * @returns Prefixed key
   */
  private getPrefixedKey(key: string): string {
    return `${STORAGE_PREFIX}${key}`;
  }

  /**
   * Obfuscates a string using Base64 encoding with a simple XOR operation
   *
   * Note: This is NOT true encryption and should not be relied upon for strong security.
   * It provides basic obfuscation to make data less readable in browser dev tools.
   * For production, use httpOnly cookies for sensitive tokens.
   *
   * @param data - Data to obfuscate
   * @returns Obfuscated string
   */
  private obfuscate(data: string): string {
    try {
      // Simple XOR with a key
      const key = 'BlueRideSecure2024';
      let obfuscated = '';

      for (let i = 0; i < data.length; i++) {
        const charCode = data.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        obfuscated += String.fromCharCode(charCode);
      }

      // Base64 encode the result
      return btoa(obfuscated);
    } catch (error) {
      console.error('Failed to obfuscate data:', error);
      return data;
    }
  }

  /**
   * Deobfuscates a string that was obfuscated using the obfuscate method
   *
   * @param data - Obfuscated string
   * @returns Original string or null if deobfuscation fails
   */
  private deobfuscate(data: string): string | null {
    try {
      // Base64 decode
      const decoded = atob(data);

      // XOR with the same key to reverse
      const key = 'BlueRideSecure2024';
      let deobfuscated = '';

      for (let i = 0; i < decoded.length; i++) {
        const charCode = decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length);
        deobfuscated += String.fromCharCode(charCode);
      }

      return deobfuscated;
    } catch {
      return null;
    }
  }
}
